import React, { useState } from 'react';
import { StatusChip } from '../ui/StatusChip';
import { Button } from '../ui/Button';
import { DocumentUploadButton } from './DocumentUploadButton';
import { AlertCircleIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, XCircleIcon } from 'lucide-react';
interface Invoice {
  id: string;
  clienteId: string;
  clienteNome: string;
  clientePiva: string;
  numero: string;
  data: string;
  importoScaduto: number;
  elegibile: boolean;
  documentiCaricati: boolean;
  documentiValidati: boolean;
}
interface CreditAssignmentTableProps {
  searchTerm: string;
  onlyEligible: boolean;
}
export function CreditAssignmentTable({
  searchTerm,
  onlyEligible
}: CreditAssignmentTableProps) {
  const [sortField, setSortField] = useState<keyof Invoice>('data');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Dati fittizi per la tabella
  const fatture: Invoice[] = [{
    id: 'inv-1',
    clienteId: '1',
    clienteNome: 'Acme Srl',
    clientePiva: '12345678901',
    numero: '2023/001',
    data: '2023-01-15',
    importoScaduto: 5000,
    elegibile: true,
    documentiCaricati: true,
    documentiValidati: true
  }, {
    id: 'inv-2',
    clienteId: '1',
    clienteNome: 'Acme Srl',
    clientePiva: '12345678901',
    numero: '2023/002',
    data: '2023-02-10',
    importoScaduto: 7500,
    elegibile: true,
    documentiCaricati: true,
    documentiValidati: false
  }, {
    id: 'inv-3',
    clienteId: '2',
    clienteNome: 'TechSolutions Spa',
    clientePiva: '98765432101',
    numero: '2023/015',
    data: '2023-02-28',
    importoScaduto: 12000,
    elegibile: true,
    documentiCaricati: false,
    documentiValidati: false
  }, {
    id: 'inv-4',
    clienteId: '3',
    clienteNome: 'Industria Meccanica Srl',
    clientePiva: '45678912301',
    numero: '2023/008',
    data: '2023-03-05',
    importoScaduto: 8750,
    elegibile: false,
    documentiCaricati: false,
    documentiValidati: false
  }, {
    id: 'inv-5',
    clienteId: '4',
    clienteNome: 'Logistica Express Srl',
    clientePiva: '78901234561',
    numero: '2023/022',
    data: '2023-03-20',
    importoScaduto: 4200,
    elegibile: true,
    documentiCaricati: true,
    documentiValidati: true
  }, {
    id: 'inv-6',
    clienteId: '5',
    clienteNome: 'Alimentari Freschi Spa',
    clientePiva: '56789012341',
    numero: '2023/031',
    data: '2023-04-05',
    importoScaduto: 9300,
    elegibile: true,
    documentiCaricati: false,
    documentiValidati: false
  }, {
    id: 'inv-7',
    clienteId: '3',
    clienteNome: 'Industria Meccanica Srl',
    clientePiva: '45678912301',
    numero: '2023/012',
    data: '2023-04-15',
    importoScaduto: 6500,
    elegibile: true,
    documentiCaricati: true,
    documentiValidati: false
  }];
  // Filtraggio per termine di ricerca
  const filteredFatture = fatture.filter(fattura => {
    const matchesSearch = fattura.clienteNome.toLowerCase().includes(searchTerm.toLowerCase()) || fattura.clientePiva.includes(searchTerm) || fattura.numero.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEligible = !onlyEligible || fattura.elegibile;
    return matchesSearch && matchesEligible;
  });
  // Ordinamento delle fatture
  const sortedFatture = [...filteredFatture].sort((a, b) => {
    if (sortField === 'clienteNome') {
      return sortDirection === 'asc' ? a.clienteNome.localeCompare(b.clienteNome) : b.clienteNome.localeCompare(a.clienteNome);
    }
    if (sortField === 'numero') {
      return sortDirection === 'asc' ? a.numero.localeCompare(b.numero) : b.numero.localeCompare(a.numero);
    }
    if (sortField === 'data') {
      return sortDirection === 'asc' ? new Date(a.data).getTime() - new Date(b.data).getTime() : new Date(b.data).getTime() - new Date(a.data).getTime();
    }
    if (sortField === 'importoScaduto') {
      return sortDirection === 'asc' ? a.importoScaduto - b.importoScaduto : b.importoScaduto - a.importoScaduto;
    }
    return 0;
  });
  // Funzione per gestire il cambio di ordinamento
  const handleSort = (field: keyof Invoice) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Formattazione dell'importo in euro
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };
  // Funzione per formattare la data
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };
  // Renderizza l'icona di ordinamento
  const renderSortIcon = (field: keyof Invoice) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-473" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-474" />;
  };
  // Renderizza lo stato di eleggibilità
  const renderEligibilityStatus = (invoice: Invoice) => {
    if (!invoice.elegibile) {
      return <div className="flex items-center text-red-600" data-id="element-475">
          <XCircleIcon className="h-5 w-5 mr-1" data-id="element-476" />
          <span data-id="element-477">Non eleggibile</span>
        </div>;
    }
    if (!invoice.documentiCaricati) {
      return <div className="flex items-center text-yellow-600" data-id="element-478">
          <AlertCircleIcon className="h-5 w-5 mr-1" data-id="element-479" />
          <span data-id="element-480">Documenti mancanti</span>
        </div>;
    }
    if (!invoice.documentiValidati) {
      return <div className="flex items-center text-yellow-600" data-id="element-481">
          <AlertCircleIcon className="h-5 w-5 mr-1" data-id="element-482" />
          <span data-id="element-483">In validazione</span>
        </div>;
    }
    return <div className="flex items-center text-green-600" data-id="element-484">
        <CheckCircleIcon className="h-5 w-5 mr-1" data-id="element-485" />
        <span data-id="element-486">Eleggibile</span>
      </div>;
  };
  return <div className="overflow-x-auto" data-id="element-487">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-488">
        <thead className="bg-gray-50" data-id="element-489">
          <tr data-id="element-490">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('clienteNome')} data-id="element-491">
              <div className="flex items-center" data-id="element-492">
                Cliente
                {renderSortIcon('clienteNome')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('numero')} data-id="element-493">
              <div className="flex items-center" data-id="element-494">
                Numero Fattura
                {renderSortIcon('numero')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('data')} data-id="element-495">
              <div className="flex items-center" data-id="element-496">
                Data Fattura
                {renderSortIcon('data')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('importoScaduto')} data-id="element-497">
              <div className="flex items-center" data-id="element-498">
                Importo Scaduto
                {renderSortIcon('importoScaduto')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-499">
              Eleggibilità
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-500">
              Documenti
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-501">
              Azioni
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-502">
          {sortedFatture.length === 0 ? <tr data-id="element-503">
              <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-504">
                Nessuna fattura trovata
              </td>
            </tr> : sortedFatture.map(fattura => <tr key={fattura.id} className="hover:bg-gray-50" data-id="element-505">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-506">
                  <div className="text-sm font-medium text-gray-900" data-id="element-507">
                    {fattura.clienteNome}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-508">
                    {fattura.clientePiva}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-509">
                  <div className="text-sm text-gray-900" data-id="element-510">{fattura.numero}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-511">
                  <div className="text-sm text-gray-900" data-id="element-512">
                    {formatDate(fattura.data)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-513">
                  <div className="text-sm font-medium text-red-600" data-id="element-514">
                    {formatCurrency(fattura.importoScaduto)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-515">
                  <div className="text-sm" data-id="element-516">
                    {renderEligibilityStatus(fattura)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-517">
                  <DocumentUploadButton fatturaId={fattura.id} documentiCaricati={fattura.documentiCaricati} data-id="element-518" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-519">
                  {fattura.elegibile && fattura.documentiCaricati && fattura.documentiValidati ? <Button data-id="element-520">Cedi Fattura</Button> : <Button disabled data-id="element-521">Cedi Fattura</Button>}
                </td>
              </tr>)}
        </tbody>
      </table>
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-522">
        <div className="flex-1 flex justify-between sm:hidden" data-id="element-523">
          <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-524">
            Precedente
          </button>
          <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-525">
            Successivo
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-526">
          <div data-id="element-527">
            <p className="text-sm text-gray-700" data-id="element-528">
              Visualizzazione{' '}
              <span className="font-medium" data-id="element-529">{sortedFatture.length}</span> di{' '}
              <span className="font-medium" data-id="element-530">{fatture.length}</span> fatture
            </p>
          </div>
          <div data-id="element-531">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-532">
              <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-533">
                <span className="sr-only" data-id="element-534">Precedente</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-535">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-536" />
                </svg>
              </button>
              <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-537">
                1
              </button>
              <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-538">
                <span className="sr-only" data-id="element-539">Successivo</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-540">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" data-id="element-541" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>;
}